<script setup lang="ts">
import { type BootstrapIconName } from "@/types/bootstrap-icons";
import { type Color } from "@/types/components/Base/Color";
import { Icon } from "@iconify/vue/offline";
import biCollection from "virtual:bi-icon-collection";

defineOptions({ name: "BaseIcon" });

const IconSizes = {
  "x-sm": 12,
  sm: 14,
  md: 16,
  body: 18,
  lg: 20,
  "x-lg": 22,
  "xx-lg": 24,
  "xxx-lg": 26,
} as const;

export type IconSizesType = keyof typeof IconSizes | number | "inherit";

export type Props = {
  iconName?: BootstrapIconName;
  iconSvg?: Component;
  size?: IconSizesType;
  color?: Color;
  bgColor?: string;
  circle?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  size: "body",
  color: "inherit",
});

const iconSize = computed(() => {
  if (props.size === "inherit") {
    return "inherit";
  }
  if (typeof props.size === "number") {
    return `${props.size}px`;
  }
  return `${IconSizes[props.size]}px`;
});

const inlineIcon = computed(() =>
  props.iconName ? biCollection.icons[props.iconName] : undefined
);

const colorClasses = {
  primary: "text-primary",
  "btn-primary": "btn-primary",
  secondary: "text-secondary",
  success: "text-success",
  info: "text-info",
  warning: "text-warning",
  danger: "text-danger",
  light: "text-white",
  dark: "text-dark",
  inherit: "i-inherit-color",
} as const;
</script>

<template>
  <span
    class="base-icon-wrapper"
    :style="{
      backgroundColor: bgColor,
    }"
    :class="[{ 'p-1': bgColor, 'i-circle': circle }]"
  >
    <Icon
      v-if="iconName && inlineIcon"
      :icon="inlineIcon"
      :data-icon-name="iconName"
      :class="[colorClasses[color]]"
      :style="{
        fontSize: iconSize,
        width: iconSize,
        height: iconSize,
      }"
    />

    <i
      v-else-if="iconName"
      :class="[`bi-${iconName}`, colorClasses[color]]"
      :data-icon-name="iconName"
      :style="{
        fontSize: iconSize,
        width: iconSize,
        height: iconSize,
      }"
    ></i>

    <component
      :is="iconSvg"
      v-else
      :class="[colorClasses[color]]"
      :style="{
        fontSize: iconSize,
        width: iconSize,
        height: iconSize,
      }"
    />
  </span>
</template>

<style scoped lang="scss">
.base-icon-wrapper {
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;

  &.i-circle {
    border-radius: 50%;
    padding: $spacer-1;
    aspect-ratio: 1 / 1; // Ensures the element is always square
    overflow: hidden;
    flex-shrink: 0;

    svg {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }
  }
}
</style>
